import React from 'react';
import {
    modals,
    cssVar,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    HStack,
    VStack,
    EmptyState,
    Button,
    Tooltip,
    Avatar,
    Spinner,
    InlineBanner,
    Text,
    DataList,
    DataItem,
    Drawer,
} from '@cad-arc';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import {
    EntityTypeEnumType,
    MasterPersonSuggestion,
    MasterVehicleSuggestion,
} from '@mark43/cad-api';
import { useApiClient } from '~/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    BootstrapMasterProfileSuggestions,
    LinkMasterProfile,
} from '@mark43/cad-api/dist/resources/MasterProfileResource';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { format, parseISO } from 'date-fns';

export type MasterProfileMatchesDrawerType = typeof MasterProfileMatchesDrawer;

function truncatedText(text?: string) {
    return !text ? undefined : (
        <div
            style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                display: 'block',
            }}
        >
            {text}
        </div>
    );
}

function EntityTile(props: { children: React.ReactNode; onClick: () => void }) {
    return (
        <HStack
            borderWidth="1px"
            borderRadius="md"
            borderColor="border.default"
            p="4"
            cursor="pointer"
            alignItems="start"
            w="100%"
            gap="16px"
            _hover={{
                backgroundColor: cssVar('arc.colors.surface.accent'),
            }}
            onClick={props.onClick}
        >
            {props.children}
        </HStack>
    );
}

function MatchedVehicle(props: {
    masterVehicleSuggestion: MasterVehicleSuggestion;
    onClick: () => void;
}) {
    return (
        <EntityTile onClick={props.onClick}>
            <Avatar size="lg" icon="Vehicle" />
            <VStack flexGrow={1000} alignItems="start">
                <Text variant="headingMd">
                    {props.masterVehicleSuggestion.plateNumber ?? 'No License Plate'}
                </Text>
                <DataList
                    variant="columned"
                    templateColumns="repeat(4, 1fr)"
                    style={{ width: '100%' }}
                >
                    <DataItem dataKey="VIN" style={{ backgroundColor: 'red' }}>
                        {truncatedText(props.masterVehicleSuggestion.vinNumber)}
                    </DataItem>
                    <div />
                    <div />
                    <DataItem dataKey="State">
                        {truncatedText(props.masterVehicleSuggestion.state)}
                    </DataItem>
                    <DataItem dataKey="Year">
                        {truncatedText(props.masterVehicleSuggestion.year?.toString())}
                    </DataItem>
                    <DataItem dataKey="Make">
                        {truncatedText(props.masterVehicleSuggestion.make)}
                    </DataItem>
                    <DataItem dataKey="Model">
                        {truncatedText(props.masterVehicleSuggestion.model)}
                    </DataItem>
                    <DataItem dataKey="Color">
                        {truncatedText(props.masterVehicleSuggestion.color)}
                    </DataItem>
                </DataList>
            </VStack>
        </EntityTile>
    );
}

function MatchedPerson(props: {
    masterPersonSuggestion: MasterPersonSuggestion;
    onClick: () => void;
}) {
    return (
        <EntityTile onClick={props.onClick}>
            <Avatar size="lg" />
            <VStack flexGrow={1000} alignItems="start">
                <Text variant="headingMd">
                    {[
                        props.masterPersonSuggestion.lastName,
                        props.masterPersonSuggestion.firstName,
                    ].join(', ')}
                </Text>
                <DataList
                    variant="columned"
                    templateColumns="repeat(4, 1fr)"
                    style={{ width: '100%' }}
                >
                    <DataItem dataKey="DOB">
                        {truncatedText(
                            props.masterPersonSuggestion.dateOfBirth
                                ? format(
                                      parseISO(props.masterPersonSuggestion.dateOfBirth),
                                      'MM/dd/yyyy'
                                  )
                                : undefined
                        )}
                    </DataItem>
                    <DataItem dataKey="DL">
                        {truncatedText(props.masterPersonSuggestion.driversLicenseNumber)}
                    </DataItem>
                    <DataItem dataKey="Sex">
                        {truncatedText(props.masterPersonSuggestion.sex)}
                    </DataItem>
                    <DataItem dataKey="Race">
                        {truncatedText(props.masterPersonSuggestion.race)}
                    </DataItem>
                    <DataItem dataKey="SSN">
                        {truncatedText(props.masterPersonSuggestion.socialSecurityNumber)}
                    </DataItem>
                    <DataItem dataKey="Height">
                        {truncatedText(props.masterPersonSuggestion.height)}
                    </DataItem>
                    <DataItem dataKey="Address">
                        {truncatedText(props.masterPersonSuggestion.address)}
                    </DataItem>
                </DataList>
            </VStack>
        </EntityTile>
    );
}

export function MasterProfileMatchesDrawer(props: {
    entityId: number;
    entityType: EntityTypeEnumType;
}): JSX.Element {
    const drawerControls = useDrawerControls();
    const apiClient = useApiClient();

    const query = useQuery({
        queryKey: ['master-entity-matches'],
        queryFn() {
            return apiClient.request<BootstrapMasterProfileSuggestions>({
                method: 'POST',
                url: 'master_profile/suggestions/',
                data: {
                    profileId: props.entityId,
                    entityType: props.entityType,
                },
            });
        },
    });

    const mutation = useMutation((args: { masterProfileId?: number }) => {
        return apiClient.request<LinkMasterProfile>({
            method: 'POST',
            url: 'master_profile/link/',
            data: {
                entityType: props.entityType,
                profileId: props.entityId,
                masterId: args.masterProfileId,
            },
        });
    });

    const missingProperties = query.data?.data.data?.missingCreationMasterProfileProperties;
    const matchedPersons = query.data?.data.data?.masterPersonSuggestions;
    const matchedVehicles = query.data?.data.data?.masterVehicleSuggestions;

    const entityIsMissingRequiredCreationProperties = !!(missingProperties?.length ?? 0 > 0);

    const createProfileButton = (
        <Button
            variant="ghost"
            leadingVisual="Add"
            isLoading={mutation.isLoading}
            isDisabled={mutation.isLoading || entityIsMissingRequiredCreationProperties}
            onClick={() => {
                modals.confirm({
                    size: 'md',
                    onConfirm() {
                        mutation
                            .mutateAsync({
                                masterProfileId: undefined,
                            })
                            .then(() => {
                                drawerControls.close();
                            });
                    },
                    title: 'Create Master Profile',
                    body: <Text>Are you sure you want to create a master profile?</Text>,
                });
            }}
        >
            Create a master profile
        </Button>
    );

    return (
        <Drawer size="lg" isOpen onClose={drawerControls.close} closeOnOverlayClick={false}>
            <DrawerHeader>Link a Master Profile</DrawerHeader>
            <DrawerBody>
                <VStack alignItems="start" width="100%" height="100%">
                    {query.isLoading ? (
                        <VStack w="100%" h="100%" alignItems="center" justifyContent="center">
                            <Spinner />
                        </VStack>
                    ) : (
                        <>
                            {query.isError ? (
                                <InlineBanner status="error">
                                    {errorOccurredWhile('loading necessary data')}
                                </InlineBanner>
                            ) : undefined}
                            {mutation.isError ? (
                                <InlineBanner status="error">
                                    {errorOccurredWhile('creating or linking to master profile')}
                                </InlineBanner>
                            ) : undefined}
                            {entityIsMissingRequiredCreationProperties ? (
                                <Tooltip
                                    content={`Cannot create master profile because the following properties are missing: ${missingProperties?.join(
                                        ', '
                                    )}`}
                                >
                                    {createProfileButton}
                                </Tooltip>
                            ) : (
                                createProfileButton
                            )}
                            {!matchedPersons?.length && !matchedVehicles?.length ? (
                                <VStack
                                    flexGrow={10000}
                                    alignItems="center"
                                    justifyContent="center"
                                    width="100%"
                                >
                                    <EmptyState
                                        visual={
                                            props.entityType === 'PERSON_PROFILE'
                                                ? 'Person'
                                                : 'Vehicle'
                                        }
                                        title="No Matched Master Profiles"
                                    />
                                </VStack>
                            ) : undefined}
                            <VStack width="100%">
                                {matchedPersons?.map((matchedPerson) => (
                                    <MatchedPerson
                                        key={matchedPerson.masterId}
                                        masterPersonSuggestion={matchedPerson}
                                        onClick={() => {
                                            modals.confirm({
                                                size: 'md',
                                                onConfirm() {
                                                    mutation
                                                        .mutateAsync({
                                                            masterProfileId: matchedPerson.masterId,
                                                        })
                                                        .then(() => {
                                                            drawerControls.close();
                                                        });
                                                },
                                                title: 'Link Master Profile',
                                                body: (
                                                    <Text>
                                                        Are you sure you want to link this master
                                                        profile?
                                                    </Text>
                                                ),
                                            });
                                        }}
                                    />
                                ))}
                                {matchedVehicles?.map((matchedVehicle) => (
                                    <MatchedVehicle
                                        key={matchedVehicle.masterId}
                                        masterVehicleSuggestion={matchedVehicle}
                                        onClick={() => {
                                            modals.confirm({
                                                size: 'md',
                                                onConfirm() {
                                                    mutation
                                                        .mutateAsync({
                                                            masterProfileId:
                                                                matchedVehicle.masterId,
                                                        })
                                                        .then(() => {
                                                            drawerControls.close();
                                                        });
                                                },
                                                title: 'Link Master Profile',
                                                body: (
                                                    <Text>
                                                        Are you sure you want to link this master
                                                        profile?
                                                    </Text>
                                                ),
                                            });
                                        }}
                                    />
                                ))}
                            </VStack>
                        </>
                    )}
                </VStack>
            </DrawerBody>
            <DrawerFooter>
                <Button
                    variant="ghost"
                    onClick={() => {
                        drawerControls.close();
                    }}
                >
                    Cancel
                </Button>
            </DrawerFooter>
        </Drawer>
    );
}
